import { Table as TanStackTable } from "@tanstack/react-table";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from "./ui/select";
import {
  Tooltip,
  TooltipProvider,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  ChevronLeft,
  ChevronsLeft,
  ChevronRight,
  ChevronsRight,
} from "lucide-react";
import { TableTheme } from "../types";

export const TablePagination = <T,>({
  table,
  pageSizeOptions = [10, 20, 30, 50, 100],
  theme,
}: {
  table: TanStackTable<T>;
  pageSizeOptions?: number[];
  theme: TableTheme;
}) => {
  return (
    <div className="flex items-center justify-between px-2 py-4">
      <div className="flex items-center space-x-2">
        <p className={`text-sm font-medium ${theme.rowText}`}>Rows per page</p>
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => {
            table.setPageSize(Number(value));
          }}
        >
          <SelectTrigger
            className={`h-8 w-[70px] rounded-full ${theme.rowEvenBg} ${theme.rowText} ${theme.buttonSecondaryBorder} ${theme.buttonSecondaryHover}`}
          >
            <SelectValue placeholder={table.getState().pagination.pageSize} />
          </SelectTrigger>
          <SelectContent
            side="top"
            className={`rounded-[1rem] ${theme.rowEvenBg}`}
          >
            {pageSizeOptions.map((pageSize) => (
              <SelectItem
                key={pageSize}
                value={`${pageSize}`}
                className={`rounded-[1rem] ${theme.rowText} ${theme.buttonSecondaryHover}`}
              >
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`hidden h-8 w-8 p-0 lg:flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !table.getCanPreviousPage()
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  title="Go to first page"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to first page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !table.getCanPreviousPage()
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  title="Go to previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to previous page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !table.getCanNextPage()
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  title="Go to next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to next page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`hidden h-8 w-8 p-0 lg:flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !table.getCanNextPage()
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  title="Go to last page"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to last page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div
          className={`flex w-[100px] items-center justify-center text-sm font-medium ${theme.rowText}`}
        >
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
      </div>
    </div>
  );
};
